import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import '../../booking/components/ConsultantCard.css';
import { IllustrativeDisclaimer } from '../../../components/IllustrativeDisclaimer';

export interface Service {
  slug: string;
  title: string;
  badge?: string;
  badgeType?: 'demand' | 'free' | 'new';
  price: string;
  image?: string;
  placeholderColor?: string;
  shortDesc: string;
  fullDesc: string;
  highlights: string[];
  category?: string;
}

interface Props {
  service: Service;
  index: number;
}

const BADGE_STYLES: Record<string, { bg: string; color: string }> = {
  demand: { bg: '#fff1f1', color: '#FF0100' },
  free: { bg: '#ECFDF5', color: '#059669' },
  new: { bg: '#EFF6FF', color: '#1D4ED8' },
};

export const ServiceCard: React.FC<Props> = ({ service, index }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const badge = service.badgeType ? BADGE_STYLES[service.badgeType] : null;

  return (
    <>
      <motion.div
        className="ccard"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: index * 0.09, ease: [0.4, 0, 0.2, 1] }}
        whileHover={{ y: -5, transition: { duration: 0.2 } }}
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setOpen(true)}
        aria-label={`View details for ${service.title}`}
      >
        <div
          className="ccard-thumb"
          style={{ background: service.image ? undefined : service.placeholderColor ?? '#f3f4f6' }}
        >
          {service.image ? (
            <img src={service.image} alt={service.title} className="ccard-img" />
          ) : (
            <div className="ccard-img" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-photo" style={{ fontSize: 32, color: 'rgba(255,255,255,0.5)' }} />
            </div>
          )}
          {badge && service.badge && (
            <span className="ccard-badge" style={{ background: badge.bg, color: badge.color }}>
              {service.badge}
            </span>
          )}
          <div className="ccard-thumb-overlay">
            <span className="ccard-thumb-hint">
              <i className="ti ti-eye" style={{ fontSize: 14 }} /> Quick view
            </span>
          </div>
        </div>

        <div className="ccard-body">
          {service.category && <span className="ccard-category">{service.category}</span>}
          <h3 className="ccard-name">{service.title}</h3>
          <p className="ccard-info">{service.shortDesc}</p>
        </div>

        <div className="ccard-foot">
          <span className="ccard-fee">{service.price}</span>
          <span className="ccard-cta">
            View details <i className="ti ti-arrow-right" style={{ fontSize: 12 }} />
          </span>
        </div>
      </motion.div>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="svc-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              onClick={() => setOpen(false)}
            />

            <div className="svc-modal-wrapper">
              <motion.div
                className="svc-modal"
                role="dialog"
                aria-modal="true"
                aria-label={service.title}
                initial={{ opacity: 0, scale: 0.93, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.93, y: 20 }}
                transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              >
                <button className="svc-modal-close" onClick={() => setOpen(false)} aria-label="Close">
                  <i className="ti ti-x" />
                </button>

                <div
                  className="svc-modal-img-wrap"
                  style={{ background: service.image ? undefined : service.placeholderColor ?? '#f4f6f8', position: 'relative' }}
                >
                  {service.image ? (
                    <img src={service.image} alt={service.title} className="svc-modal-img" />
                  ) : (
                    <div className="svc-modal-placeholder">
                      <i className="ti ti-photo" style={{ fontSize: 48, color: 'rgba(255,255,255,0.4)' }} />
                    </div>
                  )}
                  {badge && service.badge && (
                    <span className="svc-modal-badge" style={{ background: badge.bg, color: badge.color }}>
                      {service.badge}
                    </span>
                  )}
                  <IllustrativeDisclaimer className="illus-disclaimer--modal" />
                </div>

                <div className="svc-modal-body">
                  {service.category && (
                    <motion.span
                      className="svc-modal-category"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1, duration: 0.35 }}
                    >
                      {service.category}
                    </motion.span>
                  )}

                  <motion.h2
                    className="svc-modal-title"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.14, duration: 0.38 }}
                  >
                    {service.title}
                  </motion.h2>

                  <motion.p
                    className="svc-modal-desc"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.38 }}
                  >
                    {service.fullDesc}
                  </motion.p>

                  <motion.ul
                    className="svc-modal-highlights"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.27, duration: 0.38 }}
                  >
                    {service.highlights.map((h, i) => (
                      <li key={i} className="svc-modal-highlight-item">
                        <i className="ti ti-circle-check" style={{ color: '#059669', fontSize: 15, flexShrink: 0 }} />
                        {h}
                      </li>
                    ))}
                  </motion.ul>

                  <motion.div
                    className="svc-modal-foot"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.33, duration: 0.38 }}
                  >
                    <div>
                      <div className="svc-modal-price-label">Total price</div>
                      <div className="svc-modal-price">{service.price}</div>
                    </div>
                    <button
                      className="svc-modal-btn"
                      onClick={() => {
                        setOpen(false);
                        navigate(`/store/checkout/${service.slug}`);
                      }}
                    >
                      Proceed to Checkout
                      <i className="ti ti-arrow-right" style={{ fontSize: 15 }} />
                    </button>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
